import { HTTPClient, } from '@abaco/web-common/src/HTTP';
import { Dashboard, DashboardMetadata, DashboardOut, PinnableDashboard } from '../models/Dashboard';

export default class DashboardApi {
    private http: HTTPClient;
    private rootPath: string;
    private software: string;

    constructor(http: HTTPClient, rootPath: string, software: string) {
        this.http = http;
        this.rootPath = rootPath;
        this.software = software;
    }

    /**
     * Gets all dashboard visible to the current user
     */
    public async getAllDashboards(tags: string[]): Promise<DashboardMetadata[]>{
        const params = {
            tags
        }
        const res = await this.http.get(`${this.rootPath}/dashboards/${this.software}/dashboards`, {params});
        return res.data;
    }

    /**
     * Gets a dashboard based on it's ID
     * @param dashboardId the id
     */
    public async getDashboard(dashboardId: number): Promise<Dashboard>{
        const res = await this.http.get(`${this.rootPath}/dashboards/${dashboardId}`);
        return res.data;
    }

     /**
     * Gets a dashboard based on it's CODE
     * @param dashboardCode the code
     */
      public async getDashboardWithCode(dashboardCode: string): Promise<Dashboard>{
        const res = await this.http.get(`${this.rootPath}/dashboards/${this.software}/${dashboardCode}`);
        return res.data;
    }

    /**
     * Creates a new dashboard
     * @param newDashboard the dashboard
     */
    public async createDashboard(newDashboard: DashboardOut): Promise<DashboardMetadata>{
        newDashboard.software = this.software;
        const res = await this.http.post(`${this.rootPath}/dashboards`, newDashboard);
        return res.data;
    }

    /**
     * Updates a dashboard
     * @param dashboardId the id
     * @param editedDashboard the dashboard data
     */
    public async updatedDashboard(dashboardId: number, editedDashboard: DashboardOut): Promise<DashboardMetadata>{
        editedDashboard.software = this.software;
        const res = await this.http.put(`${this.rootPath}/dashboards/${dashboardId}`, editedDashboard);
        return res.data;
    }

    /**
     * Deletes a dashboard
     * @param dashboardId the ID
     */
    public async deleteDashboard(dashboardId: number): Promise<void>{
        await this.http.delete(`${this.rootPath}/dashboards/${dashboardId}`);
    }

    /**
     * Gets user's pinned dashboards
     */
    public async getPinnedDashboards(tags: string[]): Promise<Dashboard[]>{
        const params = {
            tags
        }
        const res = await this.http.get(`${this.rootPath}/pinned-dashboards/${this.software}/dashboards`, {params});
        return res.data;
    }

    /**
     * Pins a dashboard for the current user
     * @param dashboardId the ID
     */
    public async pinDashboard(dashboardId: number): Promise<DashboardMetadata>{
        const res = await this.http.put(`${this.rootPath}/pinned-dashboards/${dashboardId}`);
        return res.data;
    }

    /**
     * Pins a dashboard for the current user with code
     * @param dashboardCode the code
     */
    public async pinDashboardWithCode(dashboardCode: string): Promise<DashboardMetadata>{
        const res = await this.http.put(`${this.rootPath}/pinned-dashboards/${this.software}/${dashboardCode}`);
        return res.data;
    }

    /**
     * Unpins a previously pinned dashboard
     * @param dashboardId the ID
     */
    public async unpinDashboard(dashboardId: number): Promise<void>{
        await this.http.delete(`${this.rootPath}/pinned-dashboards/${dashboardId}`);
    }

    /**
     * Gets the last dashboard used by a user
     */
    public async getLastUsedDashboard(tags: string[]): Promise<Dashboard | null> {
        try {
            const params = {
                tags
            }
            const res = await this.http.get(`${this.rootPath}/last-used-dashboard/${this.software}/dashboards`, {params});
            return res.data;
        } catch (e) {
            if ((e as any).response?.status === 404) {
                return null;
            }
            throw e;
        }
    }

    /**
     * Gets the last dashboard id used by a user
     */
    public async getLastUsedDashboardId(tags: string): Promise<number | null> {
        try {
            const params = {tags}
            const res = await this.http.get(`${this.rootPath}/last-used-dashboard-id/${this.software}`, {params});
            return res.data;
        } catch (e) {
            if ((e as any).response?.status === 404) {
                return null;
            }
            throw e;
        }
    }

    /**
     * Sets the last dashboard used by the current user
     * @param dashboardId the ID
     */
    public async setLastUsedDashboard(dashboardId: number): Promise<void> {
        await this.http.put(`${this.rootPath}/last-used-dashboard`, {id: dashboardId});
    }

    public async canCreatePublicDash(){
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/user_registry/module_competencies/DASHBOARD/CREATE_PUBLIC`);
        return res.data.result
    }

    public async isReadOnly(){
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/user_registry/module_competencies/DASHBOARD/CREATE`);
        return !res.data.result
    }

    public async getAutomaticPinnableDashboard(tags: string[]){
        const params = {tags};
        const res = await this.http.get<PinnableDashboard[]>(`${this.rootPath}/dashboards/${this.software}/automatic-pinnable`, {params});
        return res.data;
    }
}
